import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { InputProp } from '../../utils/tsConfig';

const padZero = (num: number | string) => num.toString().padStart(2, '0');

const normalizeMonthYear = (value: any): string => {
	if (!value) return '';
	if (Object.prototype.toString.call(value) === '[object Date]' && !isNaN(value.getTime?.())) {
		return moment(value).format('YYYY-MM');
	}
	if (typeof value === 'string') {
		const iso = moment(value, moment.ISO_8601, true);
		if (iso.isValid()) return iso.format('YYYY-MM');
		const formats = ['YYYY-MM', 'MM-YYYY', 'MM/YYYY'];
		for (const fmt of formats) {
			const tryDate = moment(value, fmt, true);
			if (tryDate.isValid()) return tryDate.format('YYYY-MM');
		}
	}
	return '';
};

const splitMonthYear = (monthYearStr: string): [string, string] => {
	const m = moment(monthYearStr, 'YYYY-MM', true);
	if (!m.isValid()) return ['', ''];
	return [padZero(m.month() + 1), m.year().toString()];
};

const combineMonthYear = (y: string, m: string) => {
	if (y && m) return `${y}-${padZero(m)}`;
	return '';
};

const MonthYear: React.FC<InputProp> = ({ 
	keyId, 
	setState, 
	state, 
	isArray, 
	indexVal, 
	arrayName, 
	className, 
	returnKey, 
	fieldName, 
	onChange, 
	required = false, 
	disabled = false, 
	width = '10vw' 
}) => {
	const monthInputRef = useRef<HTMLInputElement>(null);
	const monthRef = useRef<HTMLInputElement>(null);
	const yearRef = useRef<HTMLInputElement>(null);

	const [month, setMonth] = useState('');
	const [year, setYear] = useState('');
	const [monthInvalid, setMonthInvalid] = useState(false);
	const isInternalUpdate = useRef(false);

	useEffect(() => {
		if (isInternalUpdate.current) {
			isInternalUpdate.current = false;
			return;
		}
		let rawValue = '';
		if (isArray && arrayName && indexVal !== undefined && returnKey) {
			rawValue = state?.[arrayName]?.[indexVal]?.[returnKey] || '';
		} else if (returnKey) {
			rawValue = state?.[returnKey] || '';
		} else {
			rawValue = state || '';
		}
		const [m, y] = splitMonthYear(normalizeMonthYear(rawValue));
		setMonth(m);
		setYear(y);
	}, [state]);

	useEffect(() => {
		const composed = combineMonthYear(year, month);
		const m = moment(composed, 'YYYY-MM', true);
		if (year.length === 4 && month.length >= 1 && m.isValid()) {
			isInternalUpdate.current = true;
			if (isArray && arrayName && indexVal !== undefined && returnKey && setState) {
				const updated = { ...state };
				if (!Array.isArray(updated[arrayName])) updated[arrayName] = [];
				while (updated[arrayName].length <= indexVal) updated[arrayName].push({});
				updated[arrayName][indexVal] = {
					...updated[arrayName][indexVal],
					[returnKey]: composed,
				};
				setState(updated);
			} else {
				if (onChange) onChange({ value: composed });
				if (setState) {
					if (returnKey) setState({ ...state, [returnKey]: composed });
					else setState(composed);
				}
			}
		}
	}, [month, year]);

	const onChangeMonthInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const [y, m] = (e.target.value || '').split('-');
		isInternalUpdate.current = true;
		setYear(y || '');
		setMonth(m || '');
	};

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement>, 
		setter: React.Dispatch<React.SetStateAction<string>>, 
		maxLength: number, 
		nextRef?: React.RefObject<HTMLInputElement>, 
		type?: 'month'
	) => {
		let val = e.target.value.replace(/\D/g, '').slice(0, maxLength);
		let num = parseInt(val || '0');

		if (type === 'month') {
			if (num > 12) {
				setMonthInvalid(true);
				return;
			} else {
				setMonthInvalid(false);
			}
		}

		isInternalUpdate.current = true;
		setter(val);

		if (val.length === maxLength && nextRef?.current) {
			nextRef.current.focus();
		}
	};

	const openCalendar = () => {
		monthInputRef.current?.showPicker?.();
	};

	const monthYearValue = combineMonthYear(year, month);

	const inputBaseStyle = 'outline-none border rounded-md text-center h-[30px] dark:bg-[#202020f5] dark:text-[#e3d9d9f5]';

	return (
		<label className='bg-state-50 dark:bg-neutral-800 rounded-lg shadow-sm w-[250px] px-2 py-1 mx-1' key={keyId}>
			<div className='w-full font-bold dark:bg-[#202020f5] dark:text-[#ffffffcb] text-sm'>
				{fieldName.replace(/\*$/, '')}
				{fieldName.endsWith('*') && <span className='mandatory-asterisk'>*</span>}
			</div>
			<div className='relative w-[300px] flex items-center gap-2'>
				<input 
					ref={monthRef} 
					type='text' 
					inputMode='numeric' 
					pattern='\d*' 
					maxLength={2} 
					placeholder='MM' 
					value={month} 
					required={required} 
					disabled={disabled} 
					onChange={(e) => handleChange(e, setMonth, 2, yearRef, 'month')} 
					title={monthInvalid ? 'Month must be 01–12' : ''} 
					className={`${inputBaseStyle} border-${monthInvalid ? 'red-500' : 'gray-200'} w-[20%]`} 
				/>
				<input 
					ref={yearRef} 
					type='text' 
					inputMode='numeric' 
					pattern='\d*' 
					maxLength={4} 
					placeholder='YYYY' 
					value={year} 
					required={required} 
					disabled={disabled} 
					onChange={(e) => handleChange(e, setYear, 4)} 
					className={`${inputBaseStyle} border-gray-200 w-[35%]`} 
				/>
				<input 
					ref={monthInputRef} 
					type='month' 
					value={monthYearValue} 
					required={required} 
					disabled={disabled} 
					onChange={onChangeMonthInput} 
					style={{ 
						position: 'absolute', 
						top: '100%', 
						left: 0, 
						width: 0, 
						height: 0, 
						opacity: 0, 
						pointerEvents: 'none' 
					}} 
				/>
				<button 
					type='button' 
					onClick={openCalendar} 
					disabled={disabled} 
					className='flex items-center justify-center border border-gray-200 rounded-md h-[30px] w-[15%] dark:bg-[#202020f5] dark:text-[#e3d9d9f5]'
				>
					<svg 
						xmlns='http://www.w3.org/2000/svg' 
						className='h-5 w-5' 
						fill='none' 
						viewBox='0 0 24 24' 
						stroke='currentColor' 
						strokeWidth={2}
					>
						<path 
							strokeLinecap='round' 
							strokeLinejoin='round' 
							d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' 
						/>
					</svg>
				</button>
			</div>
		</label>
	);
};

export default MonthYear;